import { ParkingTariffTieredPriceDTO } from 'src/app/base/models/models.swagger.generated';
import { Component, Input, OnInit } from '@angular/core';
import { FormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { GridBaseComponent } from 'src/app/base/shared/grid-base/grid-base.component';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { ClientService } from 'src/app/base/services/client/client.service';
import { GridConstantsFactory } from 'src/app/base/shared/syncfusion/grid-constants-factory';
import { TariffPricesFormType } from '../../tariff-details-from-types';
import { SfRequestTypes } from 'src/app/base/models/syncfusion-data';
import { NgClass, DecimalPipe } from '@angular/common';
import { TieredPriceFormComponent } from './tiered-price-form/tiered-price-form.component';
import { ToolbarModule } from '@syncfusion/ej2-angular-navigations';
import { GridModule } from '@syncfusion/ej2-angular-grids';

export interface ExtendedParkingTariffTieredPriceDTO extends ParkingTariffTieredPriceDTO {
  tierRange?: string;
}

@Component({
    selector: 'app-tariff-details-prices-tiered-prices-grid',
    templateUrl: './tariff-details-prices-tiered-prices-grid.component.html',
    styleUrls: ['./tariff-details-prices-tiered-prices-grid.component.scss'],
    imports: [GridModule, ToolbarModule, TieredPriceFormComponent, NgClass, DecimalPipe, TranslateModule]
})
export class TariffDetailsPricesTieredPricesGridComponent extends GridBaseComponent implements OnInit {
  @Input() priceFormGroup: FormGroup<TariffPricesFormType>;
  @Input() maxParkingDuration: number;

  public totalTiered = 0;

  private indexToSelect = undefined;
  private newItemIndexCount = -1;

  constructor(translateService: TranslateService,
              clientService: ClientService,
              private fb: UntypedFormBuilder,) {
    super(clientService, translateService, clientService);
    this.editDialogTitle = 'i18n.domain.administration.tariff-details.price-tab.edit-dialog-title';
    this.addDialogTitle = 'i18n.domain.administration.tariff-details.price-tab.add-dialog-title';
    this.gridCommands = GridConstantsFactory.createStandardGridCommands();
  }

  public get isTotalNotEqualToMaxDuration(): boolean {
    if (!this.maxParkingDuration) {
      return false;
    }
    return this.totalTiered !== this.maxParkingDuration;
  }

  public get canMoveUp(): boolean {
    const index = this.grid?.selectedRowIndex ?? -1;
    if (index <= 0) {
      return false;
    }
    return true;
  }

  public get canMoveDown(): boolean {
    const index = this.grid?.selectedRowIndex ?? -1;
    if (index < 0 || index + 1 >= this.priceFormGroup.controls.tieredPrices.value?.length) {
      return false;
    }
    return true;
  }

  ngOnInit(): void {
    this.recalculateTieredPriceData();
  }

  public override actionBegin(args) {
    if (args.requestType === SfRequestTypes.Add) {
      let evaluationRelayIndex = 0;
      const tieredPrices = this.priceFormGroup.controls.tieredPrices.value;
      if (tieredPrices?.length > 0) {
        for(const tieredPrice of tieredPrices) {
          evaluationRelayIndex = Math.max(evaluationRelayIndex, tieredPrice.evaluationRelayIndex + 1);
        }
      }
      args.data.evaluationRelayIndex = evaluationRelayIndex;
    }
    super.actionBegin(args);
  }

  public override actionComplete(args) {
    super.actionComplete(args);
    if (args.requestType === SfRequestTypes.Save || args.requestType === SfRequestTypes.Delete) {
      this.recalculateTieredPriceData();
      this.priceFormGroup.controls.tieredPrices.updateValueAndValidity();
    } else if (args.requestType === SfRequestTypes.Refresh) {
      if (this.indexToSelect != null) {
        this.grid.selectRow(this.indexToSelect);
        this.indexToSelect = undefined;
      }
    }
  }

  public onUp() {
    const index = this.grid.selectedRowIndex;
    this.moveRow(index, index - 1);
  }

  public onDown() {
    const index = this.grid.selectedRowIndex;
    this.moveRow(index, index + 1);
  }

  protected initializeEditForm(data: ParkingTariffTieredPriceDTO): void {
    this.gridEditForm = this.fb.group({
      id: [ data.id ?? 0 ],
      amountOfMinutes: [ data.amountOfMinutes, Validators.required],
      pricePerHour: [ data.pricePerHour, Validators.required],
      evaluationRelayIndex: [data.evaluationRelayIndex],
      tierRange: [(data as any).tierRange],
      minStepInMinutes: [data.minStepInMinutes, Validators.required]
    });
  }

  protected override getDataToSave(): any {
    const data = new ParkingTariffTieredPriceDTO(this.gridEditForm.value);
    (data as any).tierRange = this.gridEditForm.controls.tierRange.value;

    if (data.id === 0) {
      data.id = this.newItemIndexCount;
      this.newItemIndexCount = this.newItemIndexCount - 1;
    }

    return data;
  }

  protected getEditDialogTitleParams(rowData: any) {
    return null;
  }

  private recalculateTieredPriceData() {
    const tieredPrices = this.priceFormGroup.controls.tieredPrices.value as ExtendedParkingTariffTieredPriceDTO[];
    this.totalTiered = 0;
    if (tieredPrices?.length > 0) {
      tieredPrices.sort((a, b) => a.evaluationRelayIndex - b.evaluationRelayIndex);
      let minutesSum = 0;
      for (const [index, tieredPrice] of tieredPrices.entries()) {
        tieredPrice.evaluationRelayIndex = index;
        const rangeStart = minutesSum > 0 ? minutesSum + 1 : minutesSum;
        const rangeEnd = minutesSum + tieredPrice.amountOfMinutes;
        tieredPrice.tierRange = rangeStart + ' - ' + rangeEnd;
        minutesSum += tieredPrice.amountOfMinutes;
      }
      this.totalTiered = minutesSum;
    }
    this.grid?.refresh();
  }

  private moveRow(sourceIndex: number, targetIndex: number) {
    if (sourceIndex < 0 || targetIndex < 0) {
      return;
    }

    const tieredPrices = this.priceFormGroup.controls.tieredPrices.value;
    if (targetIndex >= tieredPrices.length) {
      return;
    }
    const oldEvalRelayValue = tieredPrices[sourceIndex].evaluationRelayIndex;
    tieredPrices[sourceIndex].evaluationRelayIndex = tieredPrices[targetIndex].evaluationRelayIndex;
    tieredPrices[targetIndex].evaluationRelayIndex = oldEvalRelayValue;
    this.indexToSelect = targetIndex;
    this.recalculateTieredPriceData();
  }
}
